// Company logos, derived in the browser from the company's website — never
// from companies.logo. The backend's handle_company_saved trigger fills that
// column only while it is null, so it goes stale when the website changes, and
// what it stores (https://favicon.show/<domain>) answers an unknown domain with
// a generated letter tile: a fake logo. Contract: docs/contracts/company-logos.md.

/** "https://www.Example.com/about" / "example.com" -> "example.com"; null when there is no usable host. */
export function websiteHost(website: string | null | undefined): string | null {
  const t = (website ?? "").trim();
  if (!t || /\s/.test(t)) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`);
  } catch {
    return null; // not a URL: no logo, the initials tile shows instead
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.username || url.password) return null; // "mailto:a@b.com", "user@host": not a website
  const host = url.hostname.toLowerCase().replace(/\.$/, "").replace(/^www\./, "");
  return host.includes(".") ? host : null;
}

/**
 * Where to look, best first. favicon.show picks the site's apple-touch-icon or
 * largest declared icon (measured 114-512px for real firms); with
 * fallback=default it answers an unknown domain with a 16x16 generic instead of
 * a letter tile. Google's s2 endpoint is second: capped at 128px, and its
 * unknown-domain answer is a 16x16 globe (HTTP 404, but the image still loads).
 */
export function logoSources(website: string | null | undefined): string[] {
  const host = websiteHost(website);
  if (!host) return [];
  return [
    `https://favicon.show/${host}?fallback=default`,
    `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`,
  ];
}

/**
 * Both services' "no icon" answer is exactly 16x16, so anything that small is
 * treated as no logo. A real 16px-only favicon is refused too: blown up to a
 * 28-40px tile it would be a smudge, and the initials tile is honest.
 */
export function isRealLogo(naturalWidth: number, naturalHeight: number): boolean {
  return naturalWidth > 16 && naturalHeight > 16;
}

/** "Harbor & Pine" -> "HP", "atlas" -> "A", "" -> "?". */
export function initials(name: string | null | undefined): string {
  const words = (name ?? "").split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, "")).filter(Boolean);
  if (words.length === 0) return "?";
  return words.slice(0, 2).map((w) => [...w][0]!.toUpperCase()).join("");
}
